import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges } from '@angular/core';
import { debounceTime, filter, distinctUntilChanged, map, finalize, takeUntil } from 'rxjs/operators';
import { GroupMaintenanceService } from 'src/app/pages/maintenance/group-maintenance/group-maintenance.service';
import { WebUserService } from 'src/app/pages/maintenance/web-user/web-user.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { DialogService, LanguageService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { requestWebUser } from 'src/app/model/requests';
import * as _ from 'lodash';

@Component({
  selector: 'esc-web-user-dropdown',
  templateUrl: './web-user-dropdown.component.html',
  styleUrls: ['./web-user-dropdown.component.scss']
})
export class WebUserDropdownComponent implements OnInit {
  @Input() wrapperLayoutAlign;
  @Input() formWrapperFlex;
  @Input() previousData;
  @Input() requiredError;
  @Input() isRequired;
  @Input() disabled;
  @Input() groupMntDropdown;
  @Input() isMappingModule;
  @Output() optionSelected = new EventEmitter;
  @Output() errorLoading = new EventEmitter;
  
  @ViewChild('web_userAC') web_userACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) web_userACTr: MatAutocompleteTrigger;

  form: FormGroup;
  loading = {
    web_user: false,
    init: false,
    more: false,
    error: false
  };
  public listData = [];
  private currentFilters;
  private listSub: Subscription;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public webUserService: WebUserService,
    public groupMntService: GroupMaintenanceService,
    ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(requestWebUser);
    this.currentFilters.web_filter.is_mapping_module = true;
    this.initForm();
    this.detectFormChanges();
  }

  ngOnDestroy() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.form && _.get(changes, 'requiredError') && changes.requiredError.currentValue) {
      this.form.get('web_user').markAsTouched();
    }
  }

  initForm() {
    this.form = this.fb.group({
      web_user: [null, Validators.required]
    })

    if(_.get(this.previousData, 'id')) {
      let webUserObj = {
        id: _.get(this.previousData, 'user_id'),
        username: _.toUpper(_.get(this.previousData, 'username')),
        fullname: _.toUpper(_.get(this.previousData, 'fullname')) || _.toUpper(_.get(this.previousData, 'full_name')),
      }
      this.form.get('web_user').setValue(webUserObj, { emitEvent: true });
      this.form.get('web_user').disable();
      this.currentFilters.web_filter.keyword = _.toUpper(_.get(this.previousData, 'username'));
      this.getListData('edit');
    }
    else {
      this.form.get('web_user').enable();
    }
  }

  detectFormChanges() {
    this.form.get('web_user').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      this.getListData(null);
    });
  }

  getInitialValues(data) {
    this.currentFilters.web_filter.offset = 0;
    this.currentFilters.web_filter.keyword = "";
    this.listData = [];
    this.getListData(null);
  }
   
  getMoreData(name) {
    if (this.currentFilters.getMore) {
      this.currentFilters.web_filter.offset += requestWebUser.web_filter.limit;
      this.getListData(name);
    }
  }

  getListData(mode) {
    this.loading.web_user = true;
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    let filters = _.cloneDeep(this.currentFilters);
    let svcToUse = this.webUserService.getList(filters);
    
    if(this.groupMntDropdown) {
      filters.web_filter.drop_down = 1;
      filters.web_filter.is_mapping_module = false;
      filters.web_filter.group_filter_id = _.get(this.previousData, 'group_filter_id');
      svcToUse = this.groupMntService.getWebUsers(filters)
    }

    this.listSub = svcToUse.pipe(
      finalize(() => {
        this.loading.web_user = false;
        this.loading.init = false;
        this.loading.more = false;
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          if(mode == 'edit') { 
            this.errorLoading.next(true);
          }
          return;
        }
        
        if (_.has(res, 'value') && (res.value == "Success")) {
          this.listData = this.listData.concat(res.data);

          if((res.data.length > 0) && (this.listData.length < _.get(res, 'count'))) {
            this.currentFilters.getMore = true;
          }
        }

        if((_.toLower(_.get(res, 'value')) == "empty object") && (this.currentFilters.getMore == true)) {
          this.currentFilters.getMore = false;
        }

      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        if(mode == 'edit') { 
          this.errorLoading.next(true);
        }
        this.loading.web_user = false;
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  filterListdata() {
    let webUserObj = {};
    webUserObj = this.listData.filter(e => {
      return e.id == _.get(this.previousData, 'user_id')
    })
    this.form.get('web_user').setValue(webUserObj[0]);
    this.autoCmpltClosed();
  }

  displayFn(data?: any): string | undefined {
    if(data) {
      return  (_.get(data, 'username') || 'Undefined') + ' - ' + (_.get(data, 'fullname') || 'Undefined');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];

    if (_.isObject(_.get(this.form.get('web_user'), 'value'))) {
      this.optionSelected.next(_.get(this.form.get('web_user'), 'value'))
    }
  }

  clearForm(ev: Event) {
    this.form.get('web_user').setValue(null, { emitEvent: false });
    this.optionSelected.next(null)
    // ev.stopPropagation();
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  checkAutocompleteObject(control, objectKey) {
    if ((!_.isObject(control.controls[objectKey].value) || (control.controls[objectKey].value.id == null && control.controls[objectKey].value.code != 'all')) && (control.controls[objectKey].value && control.controls[objectKey].value != "")) {
      control.controls[objectKey].setErrors({
        "notExisting": true
      });
      
      this.optionSelected.next(null)
    }
  }
}

const INITIAL_OPTIONS = {
  web_filter: {
    limit: 50,
    offset: 0,
    keyword: "",
    end_date: "",
    status: null,
    role_id: 0,
    role_code: localStorage.getItem("userRole"),
    is_asc: 1,
    drop_down: 0
  }
}